import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthBusinessTwin {
  id: string;
  twinType: string;
  name: string;
  status: string;
  baselineStateJson: string;
  intelligenceSnapshotJson: string;
  catalogRefJson: string;
  salesRefJson: string;
  externalTwinInstanceId?: string | null;
  boundary: string;
  disclaimer: string;
  lastRefreshedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
}

export interface GrowthScenario {
  id: string;
  businessTwinBindingId: string;
  name: string;
  status: string;
  whatIfJson: string;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  campaignId?: string | null;
  linkedOptimizationRunId?: string | null;
  simulateResultJson?: string | null;
  twinSimulationId?: string | null;
  boundary: string;
  disclaimer: string;
  correlationId?: string | null;
  advisoryRecommendationJson?: string | null;
  simulationRef: string;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
}

export interface GrowthScenarioCompareRow {
  scenarioId: string;
  name: string;
  status: string;
  boundary: string;
  leadDelta?: number | null;
  conversionDelta?: number | null;
  revenueDelta?: number | null;
  clvDelta?: number | null;
  confidenceLow: number;
  confidenceHigh: number;
  summary?: string | null;
}

export interface GrowthScenarioCompareResult {
  boundary: string;
  disclaimer: string;
  rows: GrowthScenarioCompareRow[];
  recommendedScenarioId?: string | null;
  recommendationSummary?: string | null;
}

@Injectable({ providedIn: 'root' })
export class GrowthTwinService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/twin`;

  getBusinessTwin(): Observable<GrowthBusinessTwin> {
    return this.http.get<GrowthBusinessTwin>(`${this.base}/business`);
  }

  refreshBusinessTwin(name?: string): Observable<GrowthBusinessTwin> {
    return this.http.post<GrowthBusinessTwin>(`${this.base}/business`, name ? { name } : {});
  }

  listScenarios(status?: string): Observable<GrowthScenario[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<GrowthScenario[]>(`${this.base}/scenarios`, { params });
  }

  getScenario(id: string): Observable<GrowthScenario> {
    return this.http.get<GrowthScenario>(`${this.base}/scenarios/${id}`);
  }

  createScenario(payload: {
    businessTwinBindingId?: string | null;
    name: string;
    whatIfJson: string;
    periodStartUtc?: string | null;
    periodEndUtc?: string | null;
    campaignId?: string | null;
    correlationId?: string | null;
  }): Observable<GrowthScenario> {
    return this.http.post<GrowthScenario>(`${this.base}/scenarios`, payload);
  }

  updateScenario(id: string, payload: {
    name: string;
    whatIfJson: string;
    periodStartUtc?: string | null;
    periodEndUtc?: string | null;
    campaignId?: string | null;
  }): Observable<GrowthScenario> {
    return this.http.put<GrowthScenario>(`${this.base}/scenarios/${id}`, payload);
  }

  simulateScenario(id: string, recommendationJson?: string): Observable<GrowthScenario> {
    return this.http.post<GrowthScenario>(`${this.base}/scenarios/${id}/simulate`, { recommendationJson: recommendationJson ?? null });
  }

  compareScenarios(scenarioIds: string[]): Observable<GrowthScenarioCompareResult> {
    return this.http.post<GrowthScenarioCompareResult>(`${this.base}/scenarios/compare`, { scenarioIds });
  }
}
